import { lazy } from "react";
import ErrorBoundary from "~/components/common/ErrorBoundary";
import BindingManager from "~/common/BindingManager";

const importLayout = (layout) => {
    return lazy(() => import(`~/layouts/${layout}`));
};

const importComponent = (component) => {
    const pathParts = component.split("/");
    switch (pathParts[0]) {
        case "custom": {
            pathParts.splice(0, 1);
            return lazy(() => import(`@/${pathParts.join("/")}`));
        }
        case "overrides": {
            pathParts.splice(0, 1);
            return lazy(() => import(`&/${pathParts.join("/")}`));
        }
        default:
            return lazy(() => import(`~/components/${component}`));
    }
};

const loadGlobals = (globals) => {
    return (
        <ErrorBoundary fallback={null}>
            {Object.keys(globals).map((component, i) => {
                const params = globals[component];
                const Component = importComponent(params.path);
                return <Component key={i} {...params} />
            })}
        </ErrorBoundary>
    );
};

const getComponentSettings = (component, defaultSettings) => {
    return defaultSettings.components[component];
};

const getPageName = (pathname, routes = {}) => {
    if (pathname) {
        for (let page in routes) {
            if (pathname.toLowerCase() === routes[page].toLowerCase()) {
                return page;
            }
        }
    }

    return "home";
};

const buildInternalUrl = (linkConfig, contextData, context) => {
    return BindingManager.buildUrl({
        ...linkConfig,
        url: `'${contextData.routes[linkConfig.page]}'`
    }, context)
};

const createLazyComponent = (asyncComponent) => {
    const LazyComponent = lazy(async () => {
        const Component = await asyncComponent();

        return { default: Component };
    });

    return (params) => (
        <LazyComponent {...params} />
    );
};

const convertToSortedArray = (object) => {
    if (object instanceof Array) {
        return object;
    }

    if (object instanceof Object) {
        const sortedIds = Object.keys(object).sort((a, b) => (Number(a) || 0) - (Number(b) || 0));

        return sortedIds.map((id) => object[id]).filter((config) => !!config);
    }

    return [];
};

const Utils = {
    component: importComponent,
    layout: importLayout,
    loadGlobalComponents: loadGlobals,
    componentSettings: getComponentSettings,
    pageName: getPageName,
    buildInternalUrl,
    createLazyComponent,
    convertToSortedArray
};

export const _ = global._;

export default Utils;